import orderActions from "actions/order";
import quoteActions from "actions/quote";
import { Modal, Notification, STATUS_TYPES } from "dyl-components";
import { useContext } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form } from "semantic-ui-react";
import { ACTIONS, QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";
import CatalogWizard from "shared/forms/CatalogWizard";
import { formatCartItems, formatOrder } from "shared/schemas/cart/cartSchema";
import { LocationUtils, StringUtils } from "utils";
import contactActions from "actions/contact"

const AddProducts = ({ account_id, opportunity_id, contact_id }) => {
    const dispatch = useDispatch();

    const { user_id, user_email, user_phone } = useSelector((state) => ({
        user_id: state.auth.user_id,
        user_email: state.users.userProfile?.email,
        user_phone: state.users.userProfile?.cell_phone,
    }));

    const { quoteBuilderConfig, onEditQuote, onViewOrder } =
        useContext(QuoteBuilderContext);
    const { id: idBeingUpdated, isUpsell, action } = quoteBuilderConfig;
    const { onRefreshAccountInformation } = useContext(QuoteBuilderContext);
    const { catalog_discounts = [] } = useSelector((state) => state.product_catalog);
    const onCreateOrder = async (data) => {
        try {
            const order_id = await dispatch(
                orderActions.create(
                    {
                        items: formatCartItems({
                            ...data,
                            cart: (data.cart || []).map(item => ({
                                ...item,
                                discount: catalog_discounts.find(discount => discount.product_id === item.id)?.discount || 0,
                                discount_cycle: catalog_discounts.find(discount => discount.product_id === item.id)?.remaning_billing_cycles || 0,
                            }))
                        }).items,
                    },
                    {
                        quote_id: idBeingUpdated,
                        account_id,
                        opportunity_id
                    }
                )
            );
            Notification.alert(
                "Successfully created order",
                STATUS_TYPES.SUCCESS
            );
            onViewOrder(order_id, account_id);
        } catch (e) {
            console.log(e);
            if(e?.Message === "Order location is not valid"){
                Notification.alert("Order requires valid address", STATUS_TYPES.ERROR);
            } else {
                Notification.alert("Failed to create order", STATUS_TYPES.ERROR);
            }   
        }
    };

    const onUpdateOrder = async (data) => {
        try {
            const { pricing_schedule: master_filter } = data;
            await dispatch(
                orderActions.update(
                    account_id,
                    {
                        order_items: formatOrder(data.cart.map(item => ({
                            ...item,
                            discount: catalog_discounts.find(discount => discount.product_id === item.id)?.discount || 0,
                            discount_cycle: catalog_discounts.find(discount => discount.product_id === item.id)?.remaning_billing_cycles || 0,
                            pricing_schedule: item.pricing_schedule ? master_filter || item.pricing_schedule : undefined
                        }))).map(item => ({
                            ...item,
                            addons: (item?.addons || []).map(addon => ({
                                id: addon,
                                cart_item_id: item.product.cart_item_id
                            }))
                        })),
                    },
                    null,
                    idBeingUpdated
                )
            );
            Notification.alert(
                "Successfully updated order!",
                STATUS_TYPES.SUCCESS
            );
            onViewOrder(idBeingUpdated, account_id);
        } catch (e) {
            console.log(e);
            if(e?.Message === "Order location is not valid"){
                Notification.alert("Order requires valid address", STATUS_TYPES.ERROR);
            } else {
                Notification.alert("Failed to create order", STATUS_TYPES.ERROR);
            }   
        }
    };

    const onSaveQuote = async (data) => {
        try {
            const id = await (async () => {
                if (idBeingUpdated) {
                    await dispatch(
                        quoteActions.updateQuote(
                            idBeingUpdated,
                            formatCartItems(data).items
                        )
                    );
                    return idBeingUpdated;
                }
                const contact = await (async () => {
                    if (data.contact_id) {
                        const address_label = LocationUtils.getAddressLabel(StringUtils.capitalize(data.contact_address?.label))?.toLowerCase();
                        return {
                            email: data.contact_email,
                            email_type: data.contact_email_type,
                            contact_id: data.contact_id,
                            phone: data.contact_phone,
                            phone_type: data.contact_phone_type,
                            address: data.contact_address,
                            ship_to: {
                                first_name: data.first_name,
                                last_name: data.last_name,
                                suffix: data.suffix
                            },
                            address_type: address_label
                        };
                    }
                    const contact = (
                        await dispatch(
                            quoteActions.readQuoteContactInformation(contact_id)
                        )
                    )?.contact;
                    const emails = contact?.email?.data || [];
                    const phones = contact?.phone?.data || [];
                    const locations = contact?.locations || [];
                    const ship_to = {
                        first_name: contact?.first_name,
                        last_name: contact?.last_name,
                        suffix: contact?.suffix
                    };
                    const address = locations.find(location => location.billing) || locations.find(location => location.shipping) || locations.find(location => location.main) || locations[0];
                    const email = emails.find((email) => email.main) || emails[0];
                    const phone = phones.find(phone => phone.phone) || phones[0];
                    const address_label = address?.label;
                    return {
                        email: email?.email,
                        email_type: email?.email_type,
                        phone: phone?.phone,
                        phone_type: phone?.phone_type,
                        contact_id: Number(contact_id),
                        address: address,
                        ship_to: ship_to,
                        address_type: LocationUtils.getAddressLabel(StringUtils.capitalize(address_label))?.toLowerCase()
                    };
                })();

                const user = data.from_user_id
                    ? {
                          user_email: data.from_email,
                          user_id: data.from_user_id,
                          user_phone: data.from_phone,
                          profile_info: data.profile_info,
                      }
                    : {
                          user_id,
                          user_email,
                          user_phone,
                          profile_info: true,
                      };

                return dispatch(
                    quoteActions.createQuote(
                        formatCartItems({
                            ...data,
                            cart: (data.cart || []).map(item => ({
                                ...item,
                                discount: 0
                            })),
                            contact_info: {
                                location: {
                                    ...contact,
                                    address_type: LocationUtils.getAddressLabel(StringUtils.capitalize(contact?.address_type))?.toLowerCase()
                                },
                                ship_to: contact.ship_to
                            },
                            account_id,
                            from_information: {
                                email: user.user_email,
                                from_user_id: user.user_id,
                                phone: user.user_phone,
                                profile_info: user.profile_info,
                            },
                            contact_id: Number(contact_id),
                        }),
                        {
                            opportunity_id,
                        }
                    )
                );
            })();
            dispatch(quoteActions.getOpportunityRecentQuote(opportunity_id));
            onEditQuote(id, action);
            onRefreshAccountInformation();
            dispatch(contactActions.getRecordsLimits());
        } catch (e) {
            console.log(e);
            Notification.alert(
                `Failed to ${idBeingUpdated ? "update" : "create"} quote`,
                STATUS_TYPES.ERROR
            );
        }
    };

    const {
        handleSubmit,
        formState: { isValid },
    } = useFormContext();

    const { isUpdating, isCreating } = useSelector((state) => ({
        isUpdating: state.quote.quoteBeingUpdated || state.order.orderBeingUpdated,
        isCreating:
            state.quote.isCreating ||
            state.quote.quoteContactInfoBeingRead ||
            state.order.isCreating
    }));

    const isSaving = isUpdating || isCreating;
    const isForOrder = isUpsell || action === ACTIONS.DEMO;
    const shouldHaveOpportunityId = isForOrder ? false : !Boolean(opportunity_id);
    return (
        <>
            <Modal.Content scrolling>
                <Form loading={isSaving} noValidate>
                    <CatalogWizard opportunity_id={opportunity_id} account_id={isForOrder ? account_id : null} />
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    loading={isSaving}
                    onClick={handleSubmit(
                        isForOrder
                            ? idBeingUpdated
                                ? onUpdateOrder
                                : onCreateOrder
                            : onSaveQuote
                    )}
                    disabled={
                        shouldHaveOpportunityId ||
                        !isValid ||
                        isSaving
                    }
                    primary
                >
                    Next
                </Button>
            </Modal.Actions>
        </>
    );
};

export default AddProducts;
